export interface AzureAdConfig {
  clientId: string;
  tenantId: string;
  authority: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scopes: string[];
  enableLocalAuth: boolean;
}

const DEFAULT_CLIENT_ID = 'cf659a4d-16a1-4220-ad21-ab4a14f745a3';
const DEFAULT_TENANT_ID = '5e3869d3-2dc1-470b-b5a7-6d44d4be01e5';
const DEFAULT_SCOPES = ['openid', 'profile', 'email', 'User.Read'];

function parseScopes(raw: string | undefined): string[] {
  if (!raw?.trim()) {
    return DEFAULT_SCOPES;
  }

  return raw
    .split(',')
    .map((scope) => scope.trim())
    .filter(Boolean);
}

function resolveAuthority(tenantId: string): string {
  const explicitAuthority = process.env.REACT_APP_AAD_AUTHORITY?.trim();
  if (explicitAuthority) {
    return explicitAuthority.replace(/\/$/, '');
  }

  return `https://login.microsoftonline.com/${tenantId}`;
}

function isBrowserLocalHost(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

function resolveRedirectUri(): string {
  const fromEnv = process.env.REACT_APP_AAD_REDIRECT_URI?.trim();
  if (fromEnv) {
    // Never keep a localhost redirect URI when the app is served from production.
    if (!isBrowserLocalHost() && /localhost|127\.0\.0\.1/.test(fromEnv)) {
      return `${window.location.origin}/sso-callback`;
    }
    return fromEnv;
  }

  if (!isBrowserLocalHost()) {
    return `${window.location.origin}/sso-callback`;
  }

  return 'http://localhost:3000/sso-callback';
}

function resolvePostLogoutUri(): string {
  const fromEnv = process.env.REACT_APP_AAD_POST_LOGOUT_URI?.trim();
  if (fromEnv) {
    if (!isBrowserLocalHost() && /localhost|127\.0\.0\.1/.test(fromEnv)) {
      return `${window.location.origin}/login`;
    }
    return fromEnv;
  }

  if (!isBrowserLocalHost()) {
    return `${window.location.origin}/login`;
  }

  return 'http://localhost:3000/login';
}

const clientId = process.env.REACT_APP_AAD_CLIENT_ID?.trim() || DEFAULT_CLIENT_ID;
const tenantId = process.env.REACT_APP_AAD_TENANT_ID?.trim() || DEFAULT_TENANT_ID;

export const azureAdConfig: AzureAdConfig = {
  clientId,
  tenantId,
  authority: resolveAuthority(tenantId),
  redirectUri: resolveRedirectUri(),
  postLogoutRedirectUri: resolvePostLogoutUri(),
  scopes: parseScopes(process.env.REACT_APP_AAD_SCOPES),
  enableLocalAuth: process.env.REACT_APP_ENABLE_LOCAL_AUTH === 'true',
};

export const loginRequest = {
  scopes: azureAdConfig.scopes,
};

if (process.env.NODE_ENV === 'production') {
  if (!clientId) {
    console.error('REACT_APP_AAD_CLIENT_ID is required in production builds.');
  }

  if (!tenantId) {
    console.error('REACT_APP_AAD_TENANT_ID is required in production builds.');
  }
}
